/**
 * Care-Rep Ticket queue — READ-ONLY (commit 3/4).
 *
 * Actionable sibling to AdminSupportChats: lists the support tickets created by
 * genuine handoffs (lockout / turn-cap / Haiku escalation). Work-queue order —
 * open first, oldest first — comes straight from the server. Clicking a row
 * opens the detail drill-in.
 *
 * Modelled on AdminRedemptionQueue (dark theme, hand-rolled table, status/source
 * filters, offset pagination). NO write actions in this commit — the
 * resolve/contact/award controls land in commit 4 (on the detail page).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, LifeBuoy } from 'lucide-react';
import { fetchTickets, type TicketStatus, type TicketSource } from '../../lib/adminApi';

const PAGE_SIZE = 25;

const STATUS_BADGE: Record<TicketStatus, string> = {
  open: 'bg-amber-500/15 text-amber-300',
  contacted: 'bg-sky-500/15 text-sky-300',
  resolved: 'bg-emerald-500/15 text-emerald-300',
  closed: 'bg-slate-600/30 text-slate-400',
};

const SOURCE_LABEL: Record<TicketSource, string> = {
  lockout: 'Lockout',
  turn_cap: 'Turn cap',
  haiku_escalation: 'AI escalation',
  verification_failed: 'Verification',
};

function StatusBadge({ status }: { status: TicketStatus }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${STATUS_BADGE[status]}`}>{status}</span>;
}

function SourceBadge({ source }: { source: TicketSource }) {
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase bg-slate-700 text-slate-300">{SOURCE_LABEL[source] ?? source}</span>;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export default function AdminTicketQueue() {
  const [offset, setOffset] = useState(0);
  // Default view: open tickets (work queue). 'all' shows every status, open-first.
  const [status, setStatus] = useState<string>('open');
  const [source, setSource] = useState<string>('');

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tickets', { status, source, page }],
    queryFn: () => fetchTickets({ page, limit: PAGE_SIZE, status: status || undefined, source: source || undefined }),
  });

  const tickets = data?.tickets ?? [];

  function onFilterChange(setter: (v: string) => void) {
    return (e: React.ChangeEvent<HTMLSelectElement>) => {
      setter(e.target.value);
      setOffset(0); // a filter change resets to the first page (triggers refetch)
    };
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold font-display text-white flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-primary" /> Tickets
        </h1>
        {data && <span className="text-xs text-slate-400">{data.total} total</span>}
      </div>

      {/* Filters */}
      <div className="bg-slate-900 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap">
        <select
          aria-label="Filter by status"
          value={status}
          onChange={onFilterChange(setStatus)}
          className="bg-slate-800 text-white text-sm px-3 py-1.5 rounded-lg border border-slate-700"
        >
          <option value="open">Open</option>
          <option value="contacted">Contacted</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
          <option value="all">All statuses</option>
        </select>
        <select
          aria-label="Filter by source"
          value={source}
          onChange={onFilterChange(setSource)}
          className="bg-slate-800 text-white text-sm px-3 py-1.5 rounded-lg border border-slate-700"
        >
          <option value="">All sources</option>
          <option value="lockout">Lockout</option>
          <option value="turn_cap">Turn cap</option>
          <option value="haiku_escalation">AI escalation</option>
          <option value="verification_failed">Verification</option>
        </select>
      </div>

      <div className="bg-slate-800 rounded-xl overflow-x-auto">
        {isLoading ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : !tickets.length ? (
          <div className="p-6 text-slate-400 text-sm">No tickets match the current filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Issue</th>
                <th className="text-left px-4 py-3">Summary</th>
                <th className="text-left px-4 py-3">Opened</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} className="border-t border-slate-700 hover:bg-slate-700/40 align-top">
                  <td className="px-4 py-3 space-y-1">
                    <StatusBadge status={t.status} />
                    <div><SourceBadge source={t.source} /></div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white max-w-[18rem] truncate">{t.member.email ?? t.member.display_name ?? t.member_id}</div>
                    <div className="text-xs text-slate-500">trust {t.member.trust_score} · {t.member.total_points} pts</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300"><div className="max-w-[14rem] truncate">{t.issue_type}</div></td>
                  <td className="px-4 py-3">
                    <div className="text-slate-300 max-w-[22rem] truncate">{t.summary || <span className="italic text-slate-500">(no summary)</span>}</div>
                    {t.owed_amount != null && <div className="text-[10px] text-emerald-400 mt-0.5">owed {t.owed_amount} pts</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{relativeTime(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/tickets/${t.id}`}
                      className="px-2 py-1 rounded text-xs font-medium text-slate-300 hover:text-white inline-flex items-center gap-1"
                    >
                      Detail <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">{offset + 1}–{offset + tickets.length} of {data.total}</p>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={!data.hasMore}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
